import {
  AddBoldIcon,
  Alert,
  Badge,
  Button,
  Flex,
  IconWrap,
  Text
} from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { formatErrors } from '@spa/components/FormFields/helpers'
import { FlipperFlagCondition } from '@spa/features'
import {
  FieldTimeRangeDurationLegacy,
  FieldTimeRangeDuration
} from '@spa/features/Scheduling'
import moment from 'moment'
import React, { useEffect } from 'react'
import type { FC } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'

export type BreakRow = {
  id?: number
  plannedDuration?: number
  timeRange?: string[]
}

type BreakRowsContainerProps = {
  defaultDuration?: number
  shiftTimeRange: string[]
  isShiftLocked?: boolean
  isBreakPaid?: boolean
  isManager?: boolean
  displayFloatingBreaksWarning?: boolean
}
export const BreakRowsContainer: FC<BreakRowsContainerProps> = ({
  defaultDuration,
  shiftTimeRange,
  isShiftLocked,
  isBreakPaid = true,
  isManager = false,
  displayFloatingBreaksWarning = false
}) => {
  const { t } = usePlanningFormTranslation()
  const {
    control,
    getValues,
    formState: { errors },
    trigger
  } = useFormContext<{ breaks: { value: BreakRow }[] }>()
  const { append, remove, update } = useFieldArray({
    control,
    name: 'breaks'
  })

  const breakErrors = errors?.breaks?.root?.message
  const { breaks = [] } = getValues()

  useEffect(() => {
    if (defaultDuration && !breaks.length) {
      return append({
        value: {
          plannedDuration: defaultDuration,
          timeRange: []
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDuration])

  const addBreak = () => {
    append({ value: { plannedDuration: 0, timeRange: [] } })
  }

  const handleChange = (newValue: BreakRow, index: number) => {
    update(index, { value: newValue })
    // force revalidation
    trigger('breaks')
  }

  return (
    <div className='mb-2'>
      <Text
        as='div'
        size='information'
        className='mb-2 font-medium text-neutral-800'
      >
        {t('component.breakRow.title')}
        {!isBreakPaid && (
          <Badge
            color='neutral'
            label={t('component.breakRow.titleLabel')}
            className='ml-1'
          />
        )}
      </Text>
      <Controller
        name='breaks'
        control={control}
        render={({ field: { value } }) => (
          <>
            {(value ?? []).map(
              ({ value: { id, plannedDuration, timeRange } }, index) => (
                <FlipperFlagCondition
                  key={id}
                  flag='frontend_scheduling_enable_new_timeinput'
                  active={
                    <FieldTimeRangeDuration
                      key={id}
                      highlightError={!!breakErrors}
                      timeRangeConstraint={{
                        start:
                          shiftTimeRange &&
                          moment(shiftTimeRange[0], 'hh:mm').format(),
                        end:
                          shiftTimeRange &&
                          moment(shiftTimeRange[1], 'hh:mm').format()
                      }}
                      inputName={`breaks.${index}.value`}
                      disabled={isShiftLocked}
                      onRemove={() => remove(index)}
                      isManager={isManager}
                      value={
                        value[index]?.value ?? {
                          id,
                          timeRange,
                          plannedDuration
                        }
                      }
                      onChange={(newValue: BreakRow) =>
                        handleChange(newValue, index)
                      }
                      error={formatErrors({
                        inputName: 'breaks',
                        errors,
                        index
                      })}
                    />
                  }
                  inactive={
                    <FieldTimeRangeDurationLegacy
                      key={id}
                      highlightError={!!breakErrors}
                      timeRangeConstraint={{
                        start:
                          shiftTimeRange &&
                          moment(shiftTimeRange[0], 'hh:mm').format(),
                        end:
                          shiftTimeRange &&
                          moment(shiftTimeRange[1], 'hh:mm').format()
                      }}
                      inputName={`breaks.${index}.value`}
                      disabled={isShiftLocked}
                      onRemove={() => remove(index)}
                      isManager={isManager}
                      value={
                        value[index]?.value ?? {
                          id,
                          timeRange,
                          plannedDuration
                        }
                      }
                      onChange={(newValue: BreakRow) =>
                        handleChange(newValue, index)
                      }
                      error={formatErrors({
                        inputName: 'breaks',
                        errors,
                        index
                      })}
                    />
                  }
                />
              )
            )}
          </>
        )}
      />
      <Flex className='items-start'>
        <Button
          intent='neutral-dark'
          variant='tertiary'
          leftIcon={<IconWrap icon={<AddBoldIcon />} size='xs' />}
          onClick={addBreak}
          disabled={isShiftLocked}
          data-testid='button-add-break'
        >
          {t('component.breakRow.add')}
        </Button>
      </Flex>
      {displayFloatingBreaksWarning && (
        <Alert
          intent='semantic.blue'
          switchAt='sm'
          title={t('component.breakWarning.title')}
          className='mb-2'
        >
          {t('component.breakWarning.warning')}
        </Alert>
      )}
    </div>
  )
}
